import { PageRequest } from '@/core/request/PageRequest';
import {
  InvoiceItemRelevanceScaleIndicator,
  InvoiceTaxRegime,
  InvoiceItemType,
  InvoiceOriginReferenceEntity,
  InvoiceDocumentType,
  InvoiceEmissionGoal,
  InvoiceEmissionType,
  InvoiceOperationDestination,
  InvoiceRecipientType,
  InvoiceShippingMode,
  InvoiceSituation,
  InvoiceTaxationNature,
  InvoiceTaxpayerType,
  InvoiceType,
  InvoiceTypeOfStreet,
} from './invoices.enums';

export enum InvoiceOriginCategory {
  PRIVATE = 'PRIVATE',
  INSURANCE = 'INSURANCE',
  SUPPLIER = 'SUPPLIER',
}

export interface InvoiceOriginReference {
  entity: InvoiceOriginReferenceEntity;
  entityCode: string;
  entityId: number;
  workOrderId?: number;
  invoiceCategory?: InvoiceOriginCategory;
}

export interface InvoiceItemTaxes {
  additionalValueMarginSt: number;
  applicableCreditCalculationRate: number;
  baseCofins: number;
  baseCofinsSt: number;
  baseCsll: number;
  baseFcp: number;
  baseFcpSt: number;
  baseFcpStWithheld: number;
  baseIcms: number;
  baseIcmsEffective: number;
  baseIcmsSt: number;
  baseIcmsStWithheld: number;
  baseIi: number;
  baseInterstateIcms: number;
  baseIpi: number;
  baseIssqn: number;
  basePis: number;
  basePisSt: number;
  calcPercentCofins: boolean;
  calcPercentCofinsSt: boolean;
  calcPercentPis: boolean;
  calcPercentPisSt: boolean;
  codeLegalFramingIpi: string;
  creditValueIcmsSn: number;
  csosnIcms: string;
  cstCofins: string;
  cstIcms: string;
  cstIpi: string;
  cstPis: string;
  defermentPercentage: number;
  deliveryValue: number;
  expenseValue: number;
  id: number;
  insuranceValue: number;
  issqnUnconditionalDiscount?: number | null;
  modBcIcms: number;
  modBcIcmsSt: number;
  motDesIcms: number;
  originIcms: number;
  percentFcp: number;
  percentProvisionalParticipation: number;
  quotBorneFinalConsumer: number;
  quotaBcOp: number;
  quotaCofins: number;
  quotaCofinsSt: number;
  quotaCsll: number;
  quotaDif: number;
  quotaFcp: number;
  quotaFcpSt: number;
  quotaFcpStWithheld: number;
  quotaIcms: number;
  quotaIcmsEffective: number;
  quotaIcmsSt: number;
  quotaIcmsUfDestination: number;
  quotaInterstateIcms: number;
  quotaIpi: number;
  quotaIrpj: number;
  quotaIssqn: number;
  quotaMvaSt: number;
  quotaPis: number;
  quotaPisSt: number;
  reductionBaseIcmsEffective: number;
  reductionIcms: number;
  reductionIcmsSt: number;
  taxRegime: InvoiceTaxRegime;
  ufSt: string;
  valueCofins: number;
  valueCofinsSt: number;
  valueCsll: number;
  valueCustomsExpenses: number;
  valueFcp: number;
  valueFcpSt: number;
  valueFcpStWithheld: number;
  valueIcms: number;
  valueIcmsDif: number;
  valueIcmsEffective: number;
  valueIcmsFcp: number;
  valueIcmsOp: number;
  valueIcmsOwnSubstitute: number;
  valueIcmsSt: number;
  valueIcmsStWithheld: number;
  valueIcmsUnencumbered: number;
  valueIi: number;
  valueInterstateDestinationIcms: number;
  valueInterstateOriginIcms: number;
  valueIof: number;
  valueIpi: number;
  valueIpiReturned: number;
  valueIrpj: number;
  valueIssqn: number;
  valuePis: number;
  valuePisSt: number;
  valueTotal: number;
  valueUnityCofins: number;
  valueUnityCofinsSt: number;
  valueUnityPis: number;
  valueUnityPisSt: number;
  withheld?: boolean;
}

export interface InvoiceItem {
  cest?: string;
  cfopId?: number;
  codif?: string;
  consumableId?: number;
  description?: string;
  invoiceId?: number;
  itemTaxes?: InvoiceItemTaxes;
  manufacturerMerchandiseCnpj?: string;
  ncmId?: number;
  origin?: string;
  partId?: number;
  productCodeAnp?: string;
  quantity: number;
  relevanceScaleIndicator?: InvoiceItemRelevanceScaleIndicator;
  taskId?: number;
  taxBenefitCode?: string;
  type: InvoiceItemType;
  unitMeasurement?: string;
  valueDiscount?: number | null;
  valueUnitary: number;
}

export interface InvoiceCCE {
  cancellationDate: string;
  cancellationJustification: string;
  cceList: InvoiceCCEListItem[];
  changeValue: number;
  cityId: number;
  commitmentNote: string;
  companyAdditionalData: string;
  companyId: number;
  contract: string;
  createDate: string;
  customFields: string;
  customerId: number;
  customerName: string;
  deleted: boolean;
  departureDate: string;
  description: string;
  documentType: InvoiceDocumentType;
  dueDate: string;
  emissionDate: string;
  emissionGoal: InvoiceEmissionGoal;
  emissionType: InvoiceEmissionType;
  faceToFace: boolean;
  finalConsumer: boolean;
  foreignCode: string;
  id: number;
  insurerId: number;
  insurerName: string;
  integrationId: string;
  intermediaryCityCode: string;
  intermediaryCompanyName: string;
  intermediaryCpfCnpj: string;
  intermediaryIndication: number;
  intermediaryMunicipalRegistration: string;
  invoiceType: InvoiceType;
  key: string;
  natureOfOperation: string;
  number: number;
  numberRps: string;
  occurrenceCity: string;
  operationDestination: InvoiceOperationDestination;
  orderBuy: string;
  outgoingInvoice: boolean;
  plugNotasId: string;
  printType: boolean;
  publicAgency: boolean;
  receiverSuframaRegistration: string;
  recipientType: InvoiceRecipientType;
  registrationIdentifier: string;
  rpsCompetence: string;
  sendEmail: boolean;
  serie: string;
  simpleNational: boolean;
  situation: InvoiceSituation;
  specialTaxRegime: InvoiceTaxRegime;
  suframaRegistration: string;
  supplierId: number;
  supplierName: string;
  taxAdditionalData: string;
  taxAuthorityFreeField: string;
  taxAuthorityFreeText: string;
  taxLoadPercentage: number;
  taxLoadSource: string;
  taxLoadValue: number;
  taxRegime: InvoiceTaxRegime;
  taxationNature: InvoiceTaxationNature;
  taxpayerFreeField: string;
  taxpayerFreeText: string;
  taxpayerType: InvoiceTaxpayerType;
  typeOfStreet: InvoiceTypeOfStreet;
}

export interface InvoiceCCEListItem {
  authorizationProtocol: string;
  description: string;
  eventDateTime: string;
  eventType: string;
  id: number;
  invoice: InvoiceCCE;
  nfeKey: string;
  reason: string;
  sequenceNumber: number;
  status: string;
}

export interface InvoiceDataDeliveryPlace {
  cityId: number;
  complement: string;
  cpfCnpj: string;
  district: string;
  id: number;
  number: string;
  street: string;
  zip: string;
}

export interface InvoiceDataNFEInfo {
  id: number;
  key: string;
  message: string;
  protocol: string;
  reason: string;
  receiptTime: string;
  situation: InvoiceSituation;
}

export interface InvoiceDataTransport {
  brand: string;
  carrierId: number;
  grossWeight: number;
  id: number;
  netWeight: number;
  number: string;
  plate: string;
  rntc: string;
  shippingMode: InvoiceShippingMode;
  state: string;
  volumeQuantity: number;
  volumeType: string;
}

export interface InvoiceData {
  cceList: InvoiceCCEListItem[];
  companyId: number;
  customerId: number;
  customerName: string;
  deliveryPlace: InvoiceDataDeliveryPlace;
  id: number;
  insurerId: number;
  insurerName: string;
  invoiceItems: InvoiceItem[];
  invoiceType: InvoiceType;
  nfeInfo: InvoiceDataNFEInfo;
  originReference: InvoiceOriginReference[];
  recipientType: InvoiceRecipientType;
  situation: InvoiceSituation;
  supplierId: number;
  supplierName: string;
  taxpayerType: InvoiceTaxpayerType;
  transport: InvoiceDataTransport;
  number?: string;
  serie?: string;
  emissionDate?: string;
  isInternalApiIssued: boolean;
  valueTotalInvoice?: number;
  taxpayerCode?: string;
  createDate?: string;
}

export interface InvoiceListParams extends PageRequest {
  companyId?: number;
  cpfCnpj?: string;
  customerId?: number;
  description?: string;
  emissionDate?: string;
  entity?: InvoiceOriginReferenceEntity;
  entityCode?: string[];
  entityId?: number[];
  insurerId?: number;
  invoiceType?: InvoiceType;
  model?: string;
  number?: string;
  serie?: string;
  situation?: InvoiceSituation[];
  supplierId?: number;
  showValueTotalInvoice?: boolean;
}

export interface InvoiceAdditionalData {
  id: number;
  companyAdditionalData: 'string';
  taxAdditionalData: 'string';
}
